import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  type ISignUpResult,
} from 'amazon-cognito-identity-js';
import type { SignInResult, SignUpParams, AuthUser } from './types';

/**
 * Map Cognito error codes to user-friendly messages.
 * NotAuthorizedException and UserNotFoundException return the same message
 * to prevent user enumeration attacks.
 */
const errorMessages: Record<string, string> = {
  NotAuthorizedException: 'Incorrect email or password.',
  UserNotFoundException: 'Incorrect email or password.',
  UsernameExistsException: 'An account with this email already exists.',
  CodeMismatchException: 'Invalid verification code. Please try again.',
  ExpiredCodeException: 'Verification code has expired. Please request a new one.',
  InvalidPasswordException: 'Password does not meet the requirements.',
  LimitExceededException: 'Too many attempts. Please try again later.',
  UserNotConfirmedException: 'Please verify your email before signing in.',
};

export function mapCognitoError(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code: string }).code;
    if (code in errorMessages) {
      return errorMessages[code];
    }
  }
  if (err && typeof err === 'object' && 'name' in err) {
    const name = (err as { name: string }).name;
    if (name in errorMessages) {
      return errorMessages[name];
    }
  }
  return 'An unexpected error occurred. Please try again.';
}

export function createUserPool(userPoolId: string, clientId: string): CognitoUserPool {
  return new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: clientId,
  });
}

export function createCognitoUser(email: string, userPool: CognitoUserPool): CognitoUser {
  return new CognitoUser({
    Username: email,
    Pool: userPool,
  });
}

export function authenticateUser(
  cognitoUser: CognitoUser,
  email: string,
  password: string,
): Promise<{ result: SignInResult; session?: CognitoUserSession; cognitoUser: CognitoUser }> {
  const authDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        resolve({ result: { type: 'SUCCESS' }, session, cognitoUser });
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: () => {
        resolve({ result: { type: 'NEW_PASSWORD_REQUIRED' }, cognitoUser });
      },
      totpRequired: (challengeName) => {
        resolve({ result: { type: 'MFA_REQUIRED', challengeName }, cognitoUser });
      },
      mfaSetup: () => {
        resolve({ result: { type: 'MFA_SETUP' }, cognitoUser });
      },
    });
  });
}

export function signUpUser(
  userPool: CognitoUserPool,
  params: SignUpParams,
): Promise<ISignUpResult> {
  const attributes = [
    new CognitoUserAttribute({ Name: 'email', Value: params.email }),
    new CognitoUserAttribute({ Name: 'name', Value: params.name }),
  ];

  return new Promise((resolve, reject) => {
    userPool.signUp(params.email, params.password, attributes, [], (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result!);
    });
  });
}

export function confirmRegistration(
  cognitoUser: CognitoUser,
  code: string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result as string);
    });
  });
}

export function resendConfirmationCode(cognitoUser: CognitoUser): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser.resendConfirmationCode((err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

export function forgotPassword(cognitoUser: CognitoUser): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function confirmPassword(
  cognitoUser: CognitoUser,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function completeNewPasswordChallenge(
  cognitoUser: CognitoUser,
  newPassword: string,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });
}

export function getSession(cognitoUser: CognitoUser): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err ?? new Error('No session'));
        return;
      }
      resolve(session);
    });
  });
}

/**
 * Force-refresh the Cognito session tokens using the refresh token.
 * This is needed after server-side group changes (e.g., accept-invite adds
 * the user to a Cognito group) so the ID token's cognito:groups claim
 * reflects the new group without waiting for the 1-hour token TTL.
 */
export function refreshSession(cognitoUser: CognitoUser): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err ?? new Error('No session'));
        return;
      }
      const refreshToken = session.getRefreshToken();
      cognitoUser.refreshSession(refreshToken, (refreshErr: Error | null, newSession: CognitoUserSession) => {
        if (refreshErr) {
          reject(refreshErr);
          return;
        }
        resolve(newSession);
      });
    });
  });
}

export function signOut(cognitoUser: CognitoUser): void {
  cognitoUser.signOut();
}

export function setupTOTP(cognitoUser: CognitoUser): Promise<string> {
  return new Promise((resolve, reject) => {
    cognitoUser.associateSoftwareToken({
      associateSecretCode: (secretCode) => resolve(secretCode),
      onFailure: (err) => reject(err),
    });
  });
}

export function verifyTOTP(cognitoUser: CognitoUser, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser.verifySoftwareToken(code, 'TOTP', {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Extract AuthUser from a Cognito session's ID token payload.
 */
export function extractUserFromSession(session: CognitoUserSession): AuthUser {
  const payload = session.getIdToken().decodePayload();
  const groups: string[] = payload['cognito:groups'] ?? [];

  const attributes: Record<string, string> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (typeof value === 'string') {
      attributes[key] = value;
    }
  }

  return {
    email: payload['email'] as string ?? '',
    name: payload['name'] as string ?? '',
    sub: payload['sub'] as string ?? '',
    groups: Array.isArray(groups) ? groups : [],
    attributes,
  };
}

/**
 * Send MFA code during sign-in challenge.
 */
export function sendMFACode(
  cognitoUser: CognitoUser,
  code: string,
  mfaType: string,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(err),
      },
      mfaType,
    );
  });
}
